// copy of the connector installer in
// packages/integrations/databricks-ai/frontend/src/index.tsx, with a cluster
// installer in place of the account one
import { DOCS_URLS } from "@p0security/frontend-core";
import { VALID_AWS_REGIONS } from "@p0security/integrations-aws-frontend";
import {
  GOVCLOUD_REGIONS,
  P0_DEFAULT_AWS_REGION,
} from "@p0security/integrations-aws-shared/constants";
import {
  type FrontendInstaller,
  integrationOptionProvider,
} from "@p0security/integrations-installer";
import { parseClusterId } from "@p0security/integrations-redshift-ai-shared/components";
import { REDSHIFT_AI_INTEGRATION_KEY } from "@p0security/integrations-redshift-ai-shared/constants";
import type { RedshiftAiComponents } from "@p0security/integrations-redshift-ai-shared/types";
import { installedIds } from "@p0security/shared/install/installed";
import type { NewConfig } from "@p0security/shared/integrations/shared";
import { Typography } from "@p0security/ui";

import {
  CONNECTOR_DATABASE_USER,
  PROVIDERS_TERRAFORM,
  SETUP_SQL,
  renderClusterTerraform,
  renderConnectorTerraform,
} from "./iac.ts";
import { listInstalledKeys } from "./options.ts";

const { Link, Paragraph, Text } = Typography;

type RedshiftAiInstaller =
  FrontendInstaller<RedshiftAiComponents>["components"];

// The connector's ARN is built in the `aws` partition, so GovCloud regions
// can't host it. P0's default region comes first, as the preselected one.
const CONNECTOR_REGIONS = [
  P0_DEFAULT_AWS_REGION,
  ...VALID_AWS_REGIONS.filter(
    (region) =>
      region !== P0_DEFAULT_AWS_REGION && !GOVCLOUD_REGIONS.includes(region)
  ),
];

/** Installed items of another integration. Its config isn't typed by its
 * components. */
const installedOf = (config: NewConfig | undefined, component: string) =>
  new Set(config ? installedIds<string, NewConfig>(component, config) : []);

const setupSqlHeader = (
  <Paragraph>
    Then, as a superuser, run this SQL in the database you enter below. It
    creates the connector&apos;s database user,{" "}
    <Text code>{CONNECTOR_DATABASE_USER}</Text>, which may manage users and
    roles, and hand over or drop what an agent created once its access ends.
    Click &quot;Next&quot; when it has run.
  </Paragraph>
);

const connectorInstaller: RedshiftAiInstaller["connector"] = {
  instructions: (context, awsAccountId, item) => ({
    commands: {
      iac: [
        {
          command: PROVIDERS_TERRAFORM,
          header: (
            <Paragraph>
              Your Terraform configuration needs these providers. If it already
              has a <Text code>required_providers</Text> block, add them to it.
            </Paragraph>
          ),
        },
        {
          command: renderConnectorTerraform(context, awsAccountId, item),
          header: (
            <Paragraph>
              Then add the following, apply it as an AWS admin of account{" "}
              {awsAccountId}, and click &quot;Next&quot;.
            </Paragraph>
          ),
        },
      ],
    },
    help: (
      <Paragraph>
        P0 reaches Redshift through a connector Lambda in AWS account{" "}
        {awsAccountId}. The connector signs in to each cluster through the
        Redshift Data API as its own execution role, so no password or secret is
        stored anywhere.
      </Paragraph>
    ),
  }),
  items: {
    region: {
      optionProvider: async () => CONNECTOR_REGIONS.map((id) => ({ id })),
    },
  },
  optionProvider: integrationOptionProvider("connector", [
    { component: "iam-write", integration: "aws" },
  ]),
  // The connector's AWS account is chosen from those, so without one the
  // choice would be empty, with nothing to say why.
  prerequisiteMessages: async ({ installed: { aws } }) => {
    if (!installedOf(aws, "iam-write").size)
      return (
        <div>
          Install P0&apos;s AWS IAM management for the connector&apos;s AWS
          account first: P0 invokes the connector as its role.
        </div>
      );
  },
};

const clusterInstaller: RedshiftAiInstaller["cluster"] = {
  instructions: (_context, clusterId, item) => ({
    commands: {
      iac: [
        {
          command: renderClusterTerraform(clusterId, item),
          header: (
            <Paragraph>
              Add the following to the Terraform configuration that holds the
              connector, and apply it. It lets the connector&apos;s execution
              role sign in to {parseClusterId(clusterId)?.name ?? clusterId} and
              run statements there through the Data API.
            </Paragraph>
          ),
        },
        { command: SETUP_SQL, header: setupSqlHeader },
      ],
      shell: [
        {
          command: SETUP_SQL,
          header: (
            <Paragraph>
              Apply the cluster&apos;s Terraform from the Terraform tab first:
              without it, the connector can&apos;t sign in. As a superuser, run
              this SQL in the database you enter below. It creates the
              connector&apos;s database user,{" "}
              <Text code>{CONNECTOR_DATABASE_USER}</Text>. Click
              &quot;Next&quot; when it has run.
            </Paragraph>
          ),
        },
      ],
    },
    help: (
      <>
        <Paragraph>
          For each agent&apos;s request, P0 creates an IAM role and a database
          user of its own, grants the user the roles or object privileges the
          request names, and drops both when the access ends.
        </Paragraph>
        <Paragraph>
          The connector can grant only what its user owns or holds WITH GRANT
          OPTION. Unless you run{" "}
          <Text code>REVOKE CREATE ON SCHEMA public FROM PUBLIC</Text> in each
          database, an agent can create tables in its <Text code>public</Text>{" "}
          schema with no grant.
        </Paragraph>
      </>
    ),
  }),
  items: {
    connector: {
      optionProvider: integrationOptionProvider("cluster", [
        { component: "connector", integration: REDSHIFT_AI_INTEGRATION_KEY },
      ]),
    },
  },
  prerequisiteMessages: async (context) => {
    const { aws, "aws-oidc": awsOidc } = context.installed;
    if (!installedOf(aws, "iam-write").size)
      return (
        <div>
          Install P0&apos;s AWS IAM management for the cluster&apos;s AWS
          account first: P0 creates each agent&apos;s IAM role through it.
        </div>
      );
    if (!installedOf(awsOidc, "identity").size)
      return (
        <div>
          Install P0&apos;s AWS OIDC for the cluster&apos;s AWS account first:
          each agent&apos;s MCP server signs in to AWS through it.
        </div>
      );
    if (!listInstalledKeys(context, "connector").size)
      return (
        <div>
          Install the Redshift (AI) connector first: P0 reaches each cluster
          through it.
        </div>
      );
  },
};

export const redshiftAiInstaller: FrontendInstaller<RedshiftAiComponents> = {
  components: {
    cluster: clusterInstaller,
    connector: connectorInstaller,
  },
  preamble: (
    <Paragraph>
      Install the connector first, then each Redshift provisioned cluster or
      Serverless workgroup that P0 should manage. See the{" "}
      <Link
        href={DOCS_URLS.REDSHIFT_AI_INSTALL}
        rel="noopener noreferrer"
        target="_blank"
      >
        installation guide
      </Link>{" "}
      for the access and tools each step needs.
    </Paragraph>
  ),
};
